<script setup>
// Cloudflare Turnstile widget for the fleet's public forms (contact/feedback).
// Renders only when VITE_TURNSTILE_SITE_KEY is baked into the build. Emits
// `token` with the current token ('' when expired/failed) and `unavailable`
// once, if the script never loads (ad-blocker) or the widget errors — the
// form then lets the user submit anyway and the backend decides.
//
// Canonical copy: keelara-dev-env/scripts/TurnstileWidget.vue; each app carries
// an identical copy under src/components/.
import { ref, onMounted, onBeforeUnmount } from 'vue'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''
const SCRIPT_ID = 'cf-turnstile-script'
const ONLOAD = '__keelaraTurnstileOnload'

const props = defineProps({
  timeoutMs: { type: Number, default: 12000 },
  theme: { type: String, default: 'auto' },
})
const emit = defineEmits(['token', 'unavailable'])

const el = ref(null)
const failed = ref(false)
let widgetId = null
let failTimer = null

function markUnavailable() {
  if (failed.value) return
  failed.value = true
  emit('token', '')
  emit('unavailable')
}

function render() {
  if (widgetId !== null || !window.turnstile || !el.value) return
  try {
    widgetId = window.turnstile.render(el.value, {
      sitekey: SITE_KEY,
      theme: props.theme,
      callback: (t) => { clearTimeout(failTimer); emit('token', t) },
      'expired-callback': () => emit('token', ''),
      'error-callback': () => { markUnavailable(); return true },
      'timeout-callback': () => emit('token', ''),
    })
  } catch (err) {
    console.warn('[turnstile] render failed', err)
    markUnavailable()
  }
}

onMounted(() => {
  if (!SITE_KEY) return
  // Several widgets may mount on one page (footer + a form): share one script
  // and a queue of onload callbacks.
  window.__keelaraTurnstileQueue = window.__keelaraTurnstileQueue || []
  window.__keelaraTurnstileQueue.push(render)
  if (window.turnstile) {
    render()
  } else if (!document.getElementById(SCRIPT_ID)) {
    window[ONLOAD] = () => { for (const fn of window.__keelaraTurnstileQueue) fn() }
    const s = document.createElement('script')
    s.id = SCRIPT_ID
    s.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${ONLOAD}`
    s.async = true
    s.defer = true
    s.onerror = markUnavailable
    document.head.appendChild(s)
  }
  failTimer = setTimeout(() => { if (widgetId === null) markUnavailable() }, props.timeoutMs)
})

onBeforeUnmount(() => {
  clearTimeout(failTimer)
  if (window.__keelaraTurnstileQueue) {
    window.__keelaraTurnstileQueue = window.__keelaraTurnstileQueue.filter((fn) => fn !== render)
  }
  if (widgetId !== null && window.turnstile) {
    try { window.turnstile.remove(widgetId) } catch { /* already gone */ }
  }
})

// Tokens are single-use: call after every submit attempt (success or failure).
function reset() {
  emit('token', '')
  if (widgetId !== null && window.turnstile) {
    try { window.turnstile.reset(widgetId) } catch { /* ignore */ }
  }
}

defineExpose({ reset, enabled: !!SITE_KEY })
</script>

<template>
  <div v-if="SITE_KEY" class="turnstile" data-testid="turnstile">
    <div v-show="!failed" ref="el" class="turnstile__widget" />
    <slot v-if="failed" name="fallback" />
  </div>
</template>

<style scoped>
.turnstile__widget { min-height: 65px; }
</style>
